import { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

import { PermissionId } from '@/constants/permission';
import loginInfoStore from '@/store/login-info';
import Main from '@/views/Main';

const redirectToChild = (to: RouteLocationNormalized) => {
  const matched = to.matched[to.matched.length - 1];

  let index = -1;
  for (let i = 0; i < matched.children.length; i += 1) {
    const item = matched.children[i];
    const permissionId = item?.meta?.tab?.permissionId;
    if (!permissionId || (permissionId && loginInfoStore.permission.includes(permissionId))) {
      index = i;
      break;
    }
  }
  const first = index > -1 ? matched?.children[index] : undefined;
  return first ? { name: first.name } : true;
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Main',
    component: Main,
    children: [
      // 首页
      {
        path: '',
        name: 'home',
        component: () => import('@/views/Home'),
      },
      // 预算管理报表
      {
        path: '/dataChart',
        name: 'dataChart',
        component: () => import('@/views/DataChart'),
        beforeEnter: redirectToChild,
        children: [
          {
            path: '/dataChart/budgetOverview',
            name: 'budgetOverview',
            component: () => import('@/views/DataChart/BudgetOverview'),
            meta: { tab: { title: '预算概况' } },
          },
          {
            path: '/dataChart/excessAndSurplus',
            name: 'excessAndSurplus',
            component: () => import('@/views/DataChart/ExcessAndSurplus'),
            meta: { tab: { title: '超预算及剩余预算' } },
          },
          {
            path: '/dataChart/singleCycleRatioAnalysis',
            name: 'singleCycleRatioAnalysis',
            component: () => import('@/views/DataChart/SingleCycleRatioAnalysis'),
            meta: { tab: { title: '单周期占比分析' } },
          },
          {
            path: '/dataChart/historicalCycleRatioAnalysis',
            name: 'historicalCycleRatioAnalysis',
            component: () => import('@/views/DataChart/HistoricalCycleRatioAnalysis'),
            meta: { tab: { title: '历史占比分析' } },
          },
          {
            path: '/dataChart/marketingBudgetUsageMonitoring',
            name: 'marketingBudgetUsageMonitoring',
            component: () => import('@/views/DataChart/MarketingBudgetUsageMonitoring'),
            meta: { tab: { title: '推广预算使用监控' } },
          },
        ],
      },
      // 预算总额申请管理
      {
        path: '/budgetTotalApply',
        name: 'budgetTotalApply',
        component: () => import('@/views/BudgetTotalApply'),
        beforeEnter: redirectToChild,
        children: [
          {
            path: '/budgetTotalApply/budgetApply',
            name: 'budgetApply',
            component: () => import('@/views/BudgetTotalApply/BudgetApply'),
            meta: {
              tab: { title: '子级预算总额申请', permissionId: PermissionId.budgetTotalApplyTab },
            },
          },
          {
            path: '/budgetTotalApply/budgetManage',
            name: 'budgetManage',
            component: () => import('@/views/MonthlyIncome'),
            meta: {
              tab: { title: '子级预算总额管理', permissionId: PermissionId.budgetAmountSubCreate },
            },
          },
          {
            path: '/budgetTotalApply/fooBudgetManage',
            name: 'fooBudgetManage',
            component: () => import('@/views/BudgetTotalApply/FooBudgetManage'),
            meta: {
              tab: {
                title: '父级预算总额管理',
                permissionId: PermissionId.budgetAmountParentCreate,
              },
            },
          },
          {
            path: '/budgetTotalApply/budgetApproval',
            name: 'budgetApproval',
            component: () => import('@/views/BudgetTotalApply/BudgetApproval'),
            meta: {
              tab: { title: '子级预算总额审批配置', permissionId: PermissionId.budgetApproval },
            },
          },
        ],
      },
      // 预算总额管理
      {
        path: '/budgetExpendBalance',
        name: 'budgetExpendBalance',
        component: () => import('@/views/BudgetExpendBalance'),
        beforeEnter: redirectToChild,
        children: [
          {
            path: '/budgetExpendBalance/totalDetail',
            name: 'totalDetail',
            component: () => import('@/views/BudgetExpendBalance/TotalDetail'),
            meta: { tab: { title: '子级预算总额支出明细' } },
          },
          {
            path: '/budgetExpendBalance/summaryOfMonth',
            name: 'summaryOfMonth',
            component: () => import('@/views/BudgetExpendBalance/SummaryOfMonth'),
            meta: { tab: { title: '子级预算月度支出汇总' } },
          },
          {
            path: '/budgetExpendBalance/summaryOfQuarter',
            name: 'summaryOfQuarter',
            component: () => import('@/views/BudgetExpendBalance/SummaryOfQuarter'),
            meta: { tab: { title: '子级预算季度支出汇总' } },
          },
          {
            path: '/budgetExpendBalance/SummaryOfYear',
            name: 'summaryOfYear',
            component: () => import('@/views/BudgetExpendBalance/SummaryOfYear'),
            meta: { tab: { title: '子级预算年度支出汇总' } },
          },
          {
            path: '/budgetExpendBalance/fooTotalSummary',
            name: 'fooTotalSummary',
            component: () => import('@/views/BudgetExpendBalance/FooTotalSummary'),
            meta: { tab: { title: '父级预算总额支出汇总' } },
          },
        ],
      },
      // 预算编号管理
      {
        path: '/budgetExpendNumber',
        name: 'budgetExpendNumber',
        component: () => import('@/views/BudgetExpendNumber'),
        beforeEnter: redirectToChild,
        children: [
          {
            path: '/budgetExpendNumber/budgetApplicationDetail',
            name: 'budgetApplicationDetail',
            component: () => import('@/views/BudgetExpendNumber/BudgetApplicationDetail'),
            meta: { tab: { title: '预算申请明细' } },
          },
          {
            path: '/budgetExpendNumber/numberDetail',
            name: 'numberDetail',
            component: () => import('@/views/BudgetExpendNumber/NumberDetail'),
            meta: { tab: { title: '预算支出明细' } },
          },
          {
            path: '/budgetExpendNumber/budgetExpendSummary',
            name: 'budgetExpendSummary',
            component: () => import('@/views/BudgetExpendNumber/BudgetExpendSummary'),
            meta: { tab: { title: '预算支出汇总' } },
          },
        ],
      },
      // 预算业务负责人管理
      {
        path: '/businessUser',
        name: 'businessUser',
        component: () => import('@/views/BusinessUser'),
      },
      // 财务科目管理
      {
        path: '/financeSubject',
        name: 'financeSubject',
        component: () => import('@/views/FinanceSubject'),
      },
      // 业务权限管理
      {
        path: '/productAuthority',
        name: 'productAuthority',
        component: () => import('@/views/ProductAuthority'),
      },
      // 系统权限管理
      {
        path: '/systemAuthority',
        name: 'systemAuthority',
        component: () => import('@/views/SystemAuthority'),
      },
      // 国家地区关系配置
      {
        path: '/regionsConfig',
        name: 'regionsConfig',
        component: () => import('@/views/RegionsConfig'),
      },
      // 活动中台管理
      {
        path: '/activityMiddleground',
        name: 'activityMiddleground',
        component: () => import('@/views/ActivityMiddleground'),
      },
      // 预算业务负责人管理
      {
        path: '/autoAwardBudget',
        name: 'autoAwardBudget',
        component: () => import('@/views/AutoAwardBudget'),
      },
    ],
  },
];

export default routes;
